import React, { useState, useEffect } from 'react';
import { getAllRents } from '../../api/admin';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';

// MUI Components
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';

// MUI Icons
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import DateRangeIcon from '@mui/icons-material/DateRange';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';

// Payment status chip styles
const getPaymentStatusChipStyle = (status: string) => {
  switch (status) {
    case 'COMPLETED':
      return { bg: '#E1FCEF', color: '#10B981' };
    case 'PENDING':
      return { bg: '#FFF4E5', color: '#FF8A00' };
    case 'FAILED':
      return { bg: '#FEEEF0', color: '#F43F5E' };
    case 'REFUNDED':
      return { bg: '#F0F9FF', color: '#0EA5E9' };
    case 'PARTIAL':
      return { bg: '#F0EDFF', color: '#7065F0' };
    default:
      return { bg: '#F3F4F6', color: '#6B7280' };
  }
};

// Rental status chip styles
const getRentalStatusChipStyle = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return { bg: '#E1FCEF', color: '#10B981' };
    case 'PENDING':
      return { bg: '#FFF4E5', color: '#FF8A00' };
    case 'COMPLETED':
      return { bg: '#F0EDFF', color: '#7065F0' };
    case 'CANCELLED':
      return { bg: '#FEEEF0', color: '#F43F5E' };
    case 'TOUR_BOOKED':
      return { bg: '#F0F9FF', color: '#0EA5E9' };
    default:
      return { bg: '#F3F4F6', color: '#6B7280' };
  }
};

// Format date
const formatDate = (dateString: string) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// Format currency
const formatCurrency = (amount: number) => {
  if (!amount && amount !== 0) return 'N/A';
  return `₹${amount.toLocaleString('en-IN')}`;
};

const RentManagement: React.FC = () => {
  const { admin } = useAuth();
  const [rents, setRents] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalRents, setTotalRents] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [selectedRent, setSelectedRent] = useState<any>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
  const [filterStartDate, setFilterStartDate] = useState<string>('');
  const [filterEndDate, setFilterEndDate] = useState<string>('');

  // Fetch rents
  const fetchRents = async () => {
    try {
      setLoading(true);
      const response = await getAllRents({
        status: filterStatus,
        page: page + 1,
        limit: rowsPerPage,
      });
      
      setRents(response.rents);
      setTotalRents(response.pagination.totalRents);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch rent payments');
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchRents();
  }, [page, rowsPerPage, filterStatus]);

  // Handle search
  const handleSearch = () => {
    setPage(0);
    fetchRents();
  };

  // Handle page change
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Open view dialog
  const handleOpenViewDialog = (rent: any) => {
    setSelectedRent(rent);
    setViewDialogOpen(true);
  };

  // Close view dialog
  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedRent(null);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, color: '#0F0F2D', mb: 4 }}>
        Rent Management
      </Typography>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              placeholder="Search by property or tenant..."
              variant="outlined"
              size="small"
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl size="small" fullWidth>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={filterStatus}
                label="Status"
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="PENDING">Pending</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="CANCELLED">Cancelled</MenuItem>
                <MenuItem value="TOUR_BOOKED">Tour Booked</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Start Date"
              type="date"
              size="small"
              fullWidth
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="End Date"
              type="date"
              size="small"
              fullWidth
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Button 
              variant="contained" 
              fullWidth
              onClick={handleSearch}
              sx={{
                backgroundColor: '#7065F0',
                '&:hover': {
                  backgroundColor: '#5A52C4',
                },
              }}
            >
              Search
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
          <CircularProgress size={60} sx={{ color: '#7065F0' }} />
        </Box>
      )}

      {/* Empty State */}
      {!loading && rents.length === 0 && (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 2 }}>
          <PaymentIcon sx={{ fontSize: 60, color: '#9CA3AF', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#374151', mb: 1 }}>
            No rent payments found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filter criteria
          </Typography>
        </Paper>
      )}

      {/* Rents Table */}
      {!loading && rents.length > 0 && (
        <TableContainer component={Paper} sx={{ boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', borderRadius: 2 }}>
          <Table>
            <TableHead sx={{ backgroundColor: '#F7F7FD' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Property</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Tenant</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Rent Amount</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Start Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Payment Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rents.map((rent) => {
                const rentalStatusStyle = getRentalStatusChipStyle(rent.status);
                const paymentStatusStyle = getPaymentStatusChipStyle(rent.paymentStatus);
                
                return (
                  <TableRow key={rent._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <HomeIcon fontSize="small" sx={{ color: '#6B7280', mr: 1 }} />
                        <Typography variant="body2">
                          {rent.property?.propertyName || rent.property?.propertyType || 'Unknown Property'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PersonIcon fontSize="small" sx={{ color: '#6B7280', mr: 1 }} />
                        <Typography variant="body2">
                          {rent.tenant?.name || rent.user?.name || 'Unknown Tenant'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AttachMoneyIcon fontSize="small" sx={{ color: '#6B7280', mr: 1 }} />
                        <Typography variant="body2">
                          {formatCurrency(rent.rentAmount)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <DateRangeIcon fontSize="small" sx={{ color: '#6B7280', mr: 1 }} />
                        <Typography variant="body2">
                          {formatDate(rent.startDate)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={rent.status} 
                        size="small"
                        sx={{ 
                          backgroundColor: rentalStatusStyle.bg,
                          color: rentalStatusStyle.color,
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={rent.paymentStatus} 
                        size="small"
                        sx={{ 
                          backgroundColor: paymentStatusStyle.bg,
                          color: paymentStatusStyle.color,
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Details">
                          <IconButton 
                            size="small" 
                            onClick={() => handleOpenViewDialog(rent)}
                            sx={{ color: '#6B7280' }}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalRents}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      {/* View Rent Dialog */}
      <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            Rent Details
            <IconButton 
              onClick={handleCloseViewDialog}
              sx={{ color: '#6B7280' }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRent && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Property Information
                    </Typography>
                    
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      {selectedRent.property?.propertyName || selectedRent.property?.propertyType || 'Unknown Property'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <HomeIcon fontSize="small" sx={{ color: '#9CA3AF', mr: 0.5 }} />
                      <Typography variant="body2" color="text.secondary">
                        {selectedRent.property?.flatHouseNo ? `${selectedRent.property.flatHouseNo}, ` : ''}
                        {selectedRent.property?.address || ''} 
                        {selectedRent.property?.city ? `, ${selectedRent.property.city}` : ''}
                      </Typography>
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {selectedRent.propertyType || selectedRent.property?.propertyType || ''} 
                      {selectedRent.property?.bhk ? ` • ${selectedRent.property.bhk} BHK` : ''}
                      {selectedRent.selectedRoomType ? ` • ${selectedRent.selectedRoomType}` : ''}
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Tenant Information
                    </Typography>
                    
                    <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                      {selectedRent.tenant?.name || selectedRent.user?.name || 'Unknown Tenant'}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {selectedRent.tenant?.email || selectedRent.user?.email || 'N/A'}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {selectedRent.tenant?.phone || selectedRent.user?.phone || 'N/A'}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)', mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Lease Details
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Start Date
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <DateRangeIcon fontSize="small" sx={{ color: '#6B7280', mr: 0.5 }} />
                          <Typography variant="body1">
                            {formatDate(selectedRent.startDate)}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          End Date
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <DateRangeIcon fontSize="small" sx={{ color: '#6B7280', mr: 0.5 }} />
                          <Typography variant="body1">
                            {formatDate(selectedRent.endDate)}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Lease Status
                        </Typography>
                        <Chip 
                          label={selectedRent.status} 
                          size="small"
                          sx={{ 
                            mt: 0.5,
                            backgroundColor: getRentalStatusChipStyle(selectedRent.status).bg,
                            color: getRentalStatusChipStyle(selectedRent.status).color,
                            fontWeight: 500,
                          }}
                        />
                      </Grid>
                      
                      {selectedRent.leaseDocument && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Lease Document
                          </Typography>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            startIcon={<DescriptionIcon />}
                            href={selectedRent.leaseDocument}
                            target="_blank"
                            sx={{ mt: 0.5 }}
                          >
                            View Document
                          </Button>
                        </Grid>
                      )}
                      
                      {selectedRent.isTourBooking && (
                        <Grid item xs={12}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            mt: 1, 
                            p: 1, 
                            bgcolor: '#F0F9FF', 
                            borderRadius: 1 
                          }}>
                            <InfoIcon fontSize="small" sx={{ color: '#0EA5E9' }} />
                            <Typography variant="body2" sx={{ color: '#0EA5E9' }}>
                              This was initiated as a tour booking
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
                
                <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Payment Information
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Rent Amount
                        </Typography>
                        <Typography variant="body1" fontWeight={600} color="#0F0F2D">
                          {formatCurrency(selectedRent.rentAmount)}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Security Deposit
                        </Typography>
                        <Typography variant="body1">
                          {formatCurrency(selectedRent.securityDeposit)}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Brokerage Amount
                        </Typography>
                        <Typography variant="body1">
                          {formatCurrency(selectedRent.brokerageAmount)}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Total Amount
                        </Typography>
                        <Typography variant="body1" fontWeight={600} color="#0F0F2D">
                          {formatCurrency(selectedRent.totalAmount)}
                        </Typography>
                      </Grid>
                      
                      {selectedRent.bookingAmount && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Booking Amount Paid
                          </Typography>
                          <Typography variant="body1">
                            {formatCurrency(selectedRent.bookingAmount)}
                          </Typography>
                        </Grid>
                      )}
                      
                      {selectedRent.remainingAmount && (
                        <Grid item xs={6}>
                          <Typography variant="body2" color="text.secondary">
                            Remaining Amount
                          </Typography>
                          <Typography variant="body1">
                            {formatCurrency(selectedRent.remainingAmount)}
                          </Typography>
                        </Grid>
                      )}
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Payment Method
                        </Typography>
                        <Typography variant="body1">
                          {selectedRent.paymentMethod}
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={6}>
                        <Typography variant="body2" color="text.secondary">
                          Payment Status
                        </Typography>
                        <Chip 
                          label={selectedRent.paymentStatus} 
                          size="small"
                          sx={{ 
                            mt: 0.5,
                            backgroundColor: getPaymentStatusChipStyle(selectedRent.paymentStatus).bg,
                            color: getPaymentStatusChipStyle(selectedRent.paymentStatus).color,
                            fontWeight: 500,
                          }}
                        />
                      </Grid>
                      
                      {selectedRent.paymentId && (
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Payment ID
                          </Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                            {selectedRent.paymentId}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseViewDialog}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RentManagement; 